<template>
  <section data-testid="management-view" class="ma-12">
    <v-card class="rounded-lg">
      <v-tabs v-model="tab">
        <v-tab data-testid="management-tab-brokers" class="pa-4">
          <span><v-icon left>mdi-handshake-outline</v-icon></span>
          {{$t('management.brokers.title')}}</v-tab
        >
        <v-tab data-testid="management-tab-banks" v-if="isRootBroker" class="pa-4">
          <span><v-icon left>mdi-bank-outline</v-icon></span> {{$t('management.banks.title')}}</v-tab
        >
        <v-tab data-testid="management-tab-companies" class="pa-4">
          <span><v-icon left>mdi-domain</v-icon></span> {{$t('management.companies.title')}}</v-tab
        >
        <v-tab data-testid="management-tab-users" class="pa-4">
          <span><v-icon left>mdi-account-outline</v-icon></span> {{$t('management.users.title')}}</v-tab
        >
      </v-tabs>
    </v-card>
    <v-tabs-items v-model="tab">
      <!--broker-->
      <v-tab-item>
        <management-table 
          :title="$t('management.brokers.title')" 
          :subtitle="$t('management.brokers.subtitle')" 
          :cta="$t('management.brokers.addBroker')"
          :cta2="$t('management.brokers.exportBroker')"
          :loading="loadingTable"
          :headers="brokerHeader"
          :items="brokerElements"
          :is-broker="true"
          type="broker"
          @toggle-override="toggleOverride"
          @confirm-delete="openDeleteModal"
          @add="addBrokerOrBank"
          @update="({item, action, id}) => updateBroker(item, action, id)"
          @delete="({item,type})=> openDeleteModal(item,type)"
          @export="exportBrokers"

          >
        <!-- Time Created with browser tooltip -->
        <template v-slot:item.raw_timecreate="{ item }">
          <date-time-layout :timestamp="item.raw_timecreate" />
        </template>

        <!-- First Login with browser tooltip -->
        <template v-slot:item.raw_first_login="{ item }">
          <date-time-layout :timestamp="item.raw_first_login" />
        </template>

        <!-- PARTNER chip -->
        <template v-slot:item.is_partner="{item}">
            <v-chip
              small
              v-show="item.is_partner"
              class="partner-chip"
            >
              {{ item.is_partner }}
            </v-chip>

        </template>
        <!--actions-->
        <template v-slot:item.delete="{item}">
          <div class="d-flex align-center">
              <v-btn data-testid="delete-entity-broker" @click="openDeleteModal(item, 'broker')" icon
                ><v-icon small>mdi-trash-can-outline</v-icon></v-btn
              >
              <v-btn data-testid="reset-password-broker"  @click="resetPassword(item)" icon>
                <v-icon small>mdi-lock-reset</v-icon></v-btn
              >
          </div>
        </template>

        <!--list-->
        <template v-slot:item.banklist="{ item }">
          <v-btn
            outlined
            color="primary"
            data-testid="edit-associations"
            class="broker-count-btn px-2"
            @click="openEntityBanksModal(item, 'broker')"
          >
            <v-icon left>mdi-bank-outline</v-icon>
            <span class="broker-count-text">{{ (item.banklist || []).length }}</span>
          </v-btn>
        </template>

      <template v-slot:item.partnerlist="{item}">     
          <v-select v-if="!item.is_partner || item.partmaster_id === 0" dense hide-details :items="item.partnerlist"  item-text="name" item-value="id"></v-select>
          <div v-else>-</div>
      </template>
        </management-table>
      </v-tab-item>

      <!--bank-->
      <v-tab-item v-if="isRootBroker">
        <management-table 
          :title="$t('management.banks.title')" 
          :subtitle="$t('management.banks.subtitle')" 
          :cta="$t('management.banks.addBank')"
          :loading="loadingTable"
          :headers="bankHeader"
          :items="bankElements"
          :is-broker="false"
          type="bank"
          @toggle-override="toggleOverride"
          @confirm-delete="openDeleteModal"
          @add="addBrokerOrBank"
          @update="({item, action, id}) => updateBroker(item, action, id)"
          @delete="({item,type})=> openDeleteModal(item,type)"

          >
          <!-- Time Created with browser tooltip -->
          <template v-slot:item.raw_timecreate="{ item }">
            <date-time-layout :timestamp="item.raw_timecreate" />
          </template>

          <!--actions-->
        <template v-slot:item.delete="{item}">
          <div class="d-flex align-center">
              <v-btn data-testid="delete-entity" @click="openDeleteModal(item, 'bank')" icon
                ><v-icon small>mdi-trash-can-outline</v-icon></v-btn
              >
              <v-btn @click="resetPassword(item)" icon>
                <v-icon small>mdi-lock-reset</v-icon>
              </v-btn>
          </div>
        </template>

        <!--bank mode: auto / manual / override-->
        <template v-slot:item.mode="{ item }">
          <v-btn-toggle
            :key="`${item.id}-${bankModeRedraws}`"
            data-testid="bank-mode"
            :data-current-mode="item.is_override"
            mandatory
            dense
            outlined
            color="primary"
            class="toggle-over-button"
            :disabled="loadingBankMode === item.id"
            :value="item.is_override"
            @change="(mode) => handleBankMode(mode, item.id)"
          >
            <v-btn :loading="loadingBankMode === item.id && pendingBankMode === 'bankauto'" outlined small value="bankauto">
              auto
            </v-btn>
            <v-btn :loading="loadingBankMode === item.id && pendingBankMode === 'bankmanual'" outlined small value="bankmanual">
              manual
            </v-btn>
            <v-btn :loading="loadingBankMode === item.id && pendingBankMode === 'brkoverride'" small value="brkoverride">
              override
            </v-btn>
          </v-btn-toggle>
        </template>

        <template v-slot:item.brokerlist="{ item }">
          <v-btn
            outlined
            color="primary"
            data-testid="edit-associations"
            class="broker-count-btn px-2"
            @click="openBankBrokersModal(item)"
          >
            <v-icon left>mdi-handshake-outline</v-icon>
            <span class="broker-count-text">{{ (item.brokerlist || []).length }}</span>
          </v-btn>
        </template>

        </management-table>
      </v-tab-item>

      <!--company-->
      <v-tab-item>
        <management-table 
          :title="$t('management.companies.title')" 
          :subtitle="$t('management.companies.subtitle')" 
          :loading="loadingTable"
          :headers="companyHeaders"
          :items="companyElements"
          :is-broker="false"
          type="company"
          @confirm-delete="openDeleteModal"
          @delete="({item, type})=> openDeleteModal(item, type)"
        >
          <!-- Company Name and Representative merged -->
          <template v-slot:item.name="{ item }">
            <div class="d-flex flex-column align-start">
              <span :data-entity-id="item.id" :title="item.name" class="truncated-name" style="font-weight: bold; font-size: .9rem !important;">
                {{ item.name }}
              </span>
              <span :title="item.representative_name" class="truncated-rep-name caption grey--text">
                {{ item.representative_name }}
              </span>
            </div>
          </template>

          <!-- Time Created with browser tooltip -->
          <template v-slot:item.raw_timecreate="{ item }">
            <date-time-layout :timestamp="item.raw_timecreate" />
          </template>

          <!-- Last Trade with browser tooltip -->
          <template v-slot:item.raw_lastTrade="{ item }">
            <date-time-layout :timestamp="item.raw_lastTrade" />
          </template>

          <!-- Parent Broker with Blue Chip if Partner -->
          <template v-slot:item.broker_master="{item}">
            <div v-if="item.is_broker_resolved" class="d-flex align-center">
              <v-chip
                v-if="item.is_parent_partner"
                small
                outlined
                color="primary"
                style="color: white !important; background: #1f52915e !important"
                class="mr-2"
              >
                Partner
              </v-chip>
              <span>{{ item.broker_master }}</span>
            </div>
            <span v-else-if="item.broker_master !== '-'" class="grey--text font-family-monospace">
              {{ item.broker_master }}
            </span>
            <span v-else>-</span>
          </template>

          <!-- Status with chip -->
          <template v-slot:item.status="{item}">
            <v-chip
              data-testid="company-status"
              small
              outlined
              :color="getStatusColor(item.status)"
              style="cursor: pointer;"
              @click="confirmToggleStatus(item)"
            >
              {{ $t(`broker.status.${item.status.toLowerCase()}`) }}
            </v-chip>
          </template>

          <!-- Actions -->
          <template v-slot:item.delete="{item}">
            <div class="d-flex align-center">
              <v-tooltip data-testid="delete-entity" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="openPhoneModal(item)"
                    class="mr-1"
                  >
                    <v-icon small>mdi-robot</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t('management.botModal.title') }}</span>
              </v-tooltip>
              <v-btn
                icon
                small
                color="primary"
                :disabled="!item.kyc_id"
                :loading="loadingKYC === item.id"
                @click="downloadKYC(item)"
                title="Download KYC documents"
                class="mr-1"
              >
                <v-icon small>mdi-download</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                color="primary"
                :loading="loadingKYCUpload === item.id"
                @click="triggerKYCUpload(item)"
                title="Upload KYC documents"
                class="mr-1"
              >
                <v-icon small>mdi-upload</v-icon>
              </v-btn>
              <v-btn @click="openDeleteModal(item, 'company')" icon>
                <v-icon small>mdi-trash-can-outline</v-icon>
              </v-btn>
              <v-btn @click="resetPassword(item)" icon>
                <v-icon small>mdi-lock-reset</v-icon>
              </v-btn>
            </div>
          </template>

          <!-- Associated Banks list -->
          <template v-slot:item.banklist="{ item }">
            <v-btn
              outlined
              color="primary"
              data-testid="edit-associations"
              class="broker-count-btn px-2"
              :disabled="!item.is_broker_resolved"
              @click="openEntityBanksModal(item, 'company')"
            >
              <v-icon left>mdi-bank-outline</v-icon>
              <span class="broker-count-text">{{ (item.banklist || []).length }}</span>
            </v-btn>
          </template>
        </management-table>
      </v-tab-item>

      <!--user-->
      <v-tab-item>
          <management-table 
          :title="$t('management.users.title')" 
          :loading="loadingTable"
          :headers="userHeaders"
          :items="userElements"
          :is-broker="false"
          type="user"
          >
        
          <!--actions-->
        <template v-slot:item.delete="{item}">
          <div class="d-flex align-center">
              <v-btn data-testid="delete-entity" @click="openDeleteModal(item, 'user', item.role)" icon
                ><v-icon small>mdi-trash-can-outline</v-icon></v-btn>
              <v-btn @click="resetPassword(item)" icon>
                <v-icon small>mdi-lock-reset</v-icon>
              </v-btn>
          </div>
        </template>


          <template v-slot:item.id="{ item }">
            <copyable-id :id="item.id" type="User" />
          </template>

          <template v-slot:item.broker="{ item }">
            <copyable-id :id="item.broker" type="Broker" />
          </template>

          <template v-slot:item.company="{ item }">
            <copyable-id :id="item.company" type="Company" />
          </template>


          <template v-slot:item.bank="{ item }">
            <copyable-id :id="item.bank" type="Bank" />
          </template>

          <template v-slot:item.name="{item}">
            <div class="d-flex flex-column">
              <span :data-entity-id="item.id" :title="item.user_title" style="font-weight: bold; font-size: .9rem !important;">{{ item.name }}</span>
              <span :title="item.entity_title" class="caption grey--text">{{ item.entity_name }}</span>
            </div>
          </template>

            <template v-slot:item.role="{item}">
              <v-chip  class="chip" :style="{backgroundColor: formatChipColorAndBG(item.role).background, 
                border: `1px solid ${formatChipColorAndBG(item.role).color} !important`}"    small>
                <span class="white--text" >{{ item.role.charAt(0).toUpperCase() + item.role.slice(1) }}</span>
              </v-chip>
          </template>

          <template v-slot:item.raw_timecreate="{ item }">
            <date-time-layout :timestamp="item.raw_timecreate" />
          </template>

          <template v-slot:item.raw_tos_accepted_time="{ item }">
            <date-time-layout :timestamp="item.raw_tos_accepted_time" />
          </template>

          <template v-slot:item.raw_last_online="{ item }">
            <v-chip
              v-if="isOnline(item.raw_last_online)"
              small
              outlined
              color="green"
            >
              Online
            </v-chip>
            <date-time-layout v-else :timestamp="item.raw_last_online" />
          </template>
        </management-table>
      </v-tab-item>
      
    </v-tabs-items>
    <!--create modal-->
    <create-broker-or-bank-modal
      :loading-delete="loadingDelete"
      :broker-list="visibleBrokerList"
      :bank-list="bankList"
      :typeOfCreation="creationType"
      @created="handleCreateBroker"
      v-model="createBrokerModal"
    />
    <delete-modal
      :loading="loadingDelete"
      v-model="confirmDeleteModal"
      :item-label="deleteObj.email || deleteObj.cnpj || deleteObj.id"
      @confirm="confirmDeleteEntity"
    >
      <div v-if="deleteObj.entityTitle" class="font-weight-bold red--text mb-4">
        {{ $t('deleteModal.userEntity', { entity: deleteObj.entityTitle }) }}
      </div>
      <div v-if="deleteDependentsCount" class="mb-4">
        <div class="font-weight-bold red--text mb-2">{{ $t('deleteModal.cascade') }}</div>
        <div class="delete-dependents text-body-2">
          <div v-for="partner in deleteDependents.partners" :key="partner.id">
            {{ partner.name }} <span class="grey--text">({{ $t('deleteModal.partner') }})</span>
          </div>
          <div v-for="company in deleteDependents.companies" :key="company.id">
            {{ company.name }}
            <span v-if="deletePartnerNames[company.broker]" class="grey--text">
              ({{ $t('deleteModal.clientOf', { name: deletePartnerNames[company.broker] }) }})
            </span>
          </div>
        </div>
      </div>
    </delete-modal>
    <reset-password-modal
    v-model="resetPasswordModal"
    @handle-reset-password="resetIt"
    :loading-delete="loadingReset"/>

    <!-- Status toggle confirmation modal -->
    <v-dialog v-model="confirmStatusModal" width="400">
      <v-card data-testid="confirm-status-modal" class="pa-4 d-flex align-center flex-column justify-center text-center">
        <v-card-title class="font-weight-bold">Confirm Status Change?</v-card-title>
        <v-card-subtitle class="mt-2">
          Are you sure you want to change the status of <strong>{{ statusToggleItem ? statusToggleItem.name : '' }}</strong> to 
          <span :class="statusToggleItem && statusToggleItem.status === 'Active' ? 'orange--text' : 'green--text'">
            {{ statusToggleItem && statusToggleItem.status === 'Active' ? 'Paused' : 'Active' }}
          </span>?
        </v-card-subtitle>
        <v-card-actions class="d-flex flex-gap-2 justify-end w-100 mt-4">
          <v-btn
            @click="confirmStatusModal = false"
            color="primary"
            outlined
          >
            Cancel
          </v-btn>
          <v-btn
            data-testid="confirm-status-change"
            @click="toggleCompanyStatus"
            :loading="loadingStatusToggle"
            color="primary"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      timeout="3000"
      >{{ snackbar.message }}</v-snackbar
    >

    <company-phone-link-modal
      v-model="phoneModal"
      :company="selectedPhoneCompany"
      @updated="fetchCompanyById"
    />

    <bank-brokers-modal
      v-model="bankBrokersModal"
      :bank="selectedBankForBrokers"
      :broker-list="brokerList"
      :loading="loadingBankBrokers"
      @add-broker="handleAddBankBroker"
      @remove-broker="handleRemoveBankBroker"
    />

    <entity-banks-modal
      v-model="entityBanksModal"
      :entity="selectedEntityForBanks"
      :type="selectedEntityType"
      :available-banks="selectedEntityAvailableBanks"
      :bank-list="bankList"
      :broker-list="brokerList"
      :company-list="companyList"
      :loading="selectedEntityType === 'broker' ? loadingBankBrokerId === selectedEntityForBanks?.id : loadingBankCompanyId === selectedEntityForBanks?.id"
      @add-bank="handleAddEntityBank"
      @remove-bank="handleRemoveEntityBank"
    />

    <!--kyc upload input-->
    <input
      @change="handleCompanyKYCUpload"
      multiple
      type="file"
      ref="companyDocumentInput"
      style="display: none"
      accept=".pdf"
    />
  </section>
</template>
<script>
import { mapActions, mapStores } from "pinia";
import { useExecutionStore, useSettingsStore, useAdminStore, useImportFiles, useUploadStore, MAX_KYC_FILE_SIZE } from "./../store/index";
import CreateBrokerOrBankModal from "@/components/modal/CreateBrokerOrBankModal.vue";
import convertToCNPJ from "@/utils";
import DeleteModal from "@/components/modal/DeleteModal.vue";
import ResetPasswordModal from "@/components/modal/ResetPasswordModal.vue";
import { useAuthStore } from "@/store/console";
import AdminList from "@/components/admin/AdminList.vue";
import { i18n } from "@/plugins/vuetify"
import { generateBrokerJSON } from "@/exportBrokersJson.js";
import ManagementTable from "@/components/admin/ManagementTable.vue";
import DateTimeLayout from "@/components/common/DateTimeLayout.vue";
import CompanyPhoneLinkModal from "@/components/modal/CompanyPhoneLinkModal.vue";
import BankBrokersModal from "@/components/modal/BankBrokersModal.vue";
import EntityBanksModal from "@/components/modal/EntityBanksModal.vue";
import CopyableId from "@/components/common/CopyableId.vue";
import { formatShortDate, formatTimeAndDate, isClearfxaiBroker, scopeBrokersToCurrentBroker, scopeCompaniesToCurrentBroker, scopeUsersToCurrentBroker } from "@/utils";

export default {
  mounted() {
    setInterval(() => {
      this.fetchBrokerList();
      this.fetchBankList();
      this.fetchUserList();
      this.fetchCompanyById();
    }, 2000);
  },
  components: {
    CreateBrokerOrBankModal,
    DeleteModal,
    ResetPasswordModal,
    AdminList,
    BankBrokersModal,
    EntityBanksModal,
    ManagementTable,
    DateTimeLayout,
    CompanyPhoneLinkModal,
    CopyableId
  },
  data() {
    return {
      entityBanksModal: false,
      selectedEntityForBanks: null,
      selectedEntityType: 'broker',
      bankBrokersModal: false,
      selectedBankForBrokers: null,
      loadingBankBrokers: false,
      loadingBankBrokerId: null,
      loadingBankCompanyId: null,
      phoneModal: false,
      selectedPhoneCompany: null,
      creationType: '',
      loadingKYC: null,
      loadingKYCUpload: null,
      kycUploadTargetCompany: null,
      loadingBankMode: null,
      pendingBankMode: null,
      bankModeRedraws: 0,
      snackbar: {
        show: false,
        color: "green",
        message: "",
      },
      openSelectedId:null,
      bankElement : {},
      bankModal: false,
      loadingDelete: false,
      loadingReset: false,
      loadingTable:true,
      confirmDeleteModal: false,
      overrideSelected: [],
      brokerList: [],
      bankList: [],
      userList: [],
      companyList: [],
      deleteObj: {},
      deleteDependents: null,
      createBrokerModal: false,
      tab: null,
      resetPasswordModal: false,
      confirmStatusModal: false,
      statusToggleItem: null,
      loadingStatusToggle: false,
      brokerHeader: [        
        {
          text: this.$t("management.brokers.headers.timeCreated"),
          value: "raw_timecreate",
          width: "130px"
        },
        {
            text: "First Login",
            value: "raw_first_login",
            width: "130px"
          },
        { text: "", value: "is_partner", sortable: false },
        { text: "CNPJ", value: "cnpj", width: "200px" },
        { text: this.$t("management.brokers.headers.name"), value: "name" },
        { text: this.$t("broker.labels.email"), value: "email" },
        { text: this.$t("management.brokers.headers.originalBroker"), value: "broker_master" },
        { text: this.$t("management.brokers.headers.spread"), value: "spread", sortable: false },
        { text: this.$t("management.brokers.headers.spreadType"), value: "spread_type", sortable: false, width: "110px" },
        { text: this.$t("management.brokers.headers.partnerList"), value: "partnerlist", sortable: false, width:"110px"  },
        { text: this.$t("management.brokers.headers.bankList"), value: "banklist", sortable: false, width:"110px" },

        {
          text: this.$t("broker.clientPortfolio.headers.actions"),
          value: "delete",
          sortable: false,
        },
      ],
      bankHeader: [
        {
          text: "Date Created",
          value: "raw_timecreate",
        },
        { text: "Code", value: "bankcode" },
        { text: "CNPJ", value: "cnpj" },
        { text: "Name", value: "name" },
        { text: this.$t("broker.labels.email"), value: "email" },
        { text: "Mode", value: "mode", sortable: false, width: "220px" },
        { text: "Broker List", value: "brokerlist" },

        {
          text: this.$t("broker.clientPortfolio.headers.actions"),
          value: "delete",
          sortable: false,
        },
      ],
      userHeaders: [
        { text: '', value: 'role' },
        {
          text: this.$t("management.brokers.headers.timeCreated"),
          value: "raw_timecreate",
          width: "130px",
          sortable: true
        },
        { text: 'Last Online', value: 'raw_last_online', width: '130px' },
        { text: 'TOS Accepted', value: 'raw_tos_accepted_time', width: '130px', sortable: true },
        { text: 'ID', value: 'id' },
        { text: 'CNPJ', value: 'cnpj' },
        { text: 'Name', value: 'name' },
        { text: 'Email', value: 'email' },
        { text: 'Broker ID', value: 'broker' },
        { text: 'Company ID', value: 'company' },
        { text: 'Bank ID', value: 'bank' },
        {
          text: this.$t("broker.clientPortfolio.headers.actions"),
          value: "delete",
          sortable: false,
        },
      ],
      companyHeaders: [
        {
          text: this.$t("management.brokers.headers.timeCreated"),
          value: "raw_timecreate",
          width: "130px"
        },
        { text: this.$t("broker.labels.companyName"), value: "name" },
        { text: "CNPJ", value: "cnpj", width: "200px" },
        { text: this.$t("broker.labels.email"), value: "email" },
        { text: this.$t("management.brokers.headers.originalBroker"), value: "broker_master" },
        { text: this.$t("broker.clientPortfolio.headers.status"), value: "status" },
        { text: this.$t("broker.clientPortfolio.headers.lastTrade"), value: "raw_lastTrade" },
        { text: this.$t("management.brokers.headers.bankList"), value: "banklist", sortable: false, width: "110px" },
        {
          text: this.$t("broker.clientPortfolio.headers.actions"),
          value: "delete",
          sortable: false,
        },
      ]
    };
  },
    watch: {
    tab() {
      if (this.openSelectedId) {
        const ref = this.$refs['bankSelect-' + this.openSelectedId]
        const select = Array.isArray(ref) ? ref[0] : ref
        if (select) select.isMenuActive = false
        this.openSelectedId = null
      }
    }
  },
  computed: {
    ...mapStores(useSettingsStore),
    selectedEntityAvailableBanks() {
      if (!this.selectedEntityForBanks) return []
      if (this.selectedEntityType === 'broker') {
        return this.availableBanksForBroker(this.selectedEntityForBanks)
      }
      return this.availableBanksForCompany(this.selectedEntityForBanks)
    },
    banksToAdd:{
      get(){
        new Set(this.bankList.map(b => b.id))
      },
      set(bankId)
      {
        this.updateBroker(this.selectedBroker, 'add', bankId)
      }
    },
    userElements(){
      const list = this.visibleUserList;
      return list.filter(li => li.id).map((element) => {
        let cnpj = '-'
        let entityName = '-'

        if (element.role?.includes('broker') || element.role?.includes('partner')) {
          const brokerObj = this.brokerList.find(b => b.id === element.broker)
          cnpj = brokerObj?.cnpj || '-'
          entityName = brokerObj?.name || '-'
        }
        else if (element.role?.includes('bank')) {
          const bankObj = this.bankList.find(b => b.id === element.bank)
          cnpj = bankObj?.cnpj || '-'
          entityName = bankObj?.name || '-'
        }
        else {
          const companyObj = this.companyList.find(c => c.id === element.company)
          cnpj = companyObj?.cnpj || '-'
          entityName = companyObj?.name || '-'
        }

        let entityPrefix = 'Company'
        if (element.role?.includes('broker') || element.role?.includes('partner')) {
          entityPrefix = 'Broker'
        } else if (element.role?.includes('bank')) {
          entityPrefix = 'Bank'
        }

        const date = this.formatDate(element.timecreate);
        const acceptedTime = element.tos && element.tos.acknowledgedAt ? element.tos.acknowledgedAt : null
        const result = {}
        result.id = element.id
        result.name = element.name
        result.email = element.email
        result.broker = element.broker ?? '-'
        result.company = element.company ?? '-'
        result.bank = element.bank ?? '-'
        result.role = element.role
        result.entity_name = entityName
        result.entity_title = `(${entityPrefix}) ${entityName}`
        result.user_title = `(User) ${element.name}`
        result.cnpj = convertToCNPJ(cnpj)
        result.timecreate = date
        result.raw_timecreate = element.timecreate
        result.last_online = element.last_online ? this.formatDate(element.last_online) : '-'
        result.raw_last_online = element.last_online
        result.tos_accepted_time = acceptedTime ? this.formatDate(acceptedTime) : '-'
        result.raw_tos_accepted_time = acceptedTime
        return result
      })
    },
    //bankList(){}
    isRootBroker() {
      return isClearfxaiBroker();
    },
    // Only ClearFX AI sees every broker. Any other broker is limited to itself
    // and to the partners it masters, so nobody browses a competitor's book.
    // `brokerList` stays whole on purpose: the company/user tabs still resolve
    // parent-broker names through it.
    visibleBrokerList() {
      return scopeBrokersToCurrentBroker(this.brokerList);
    },
    // Companies follow the brokers: a broker sees the ones sitting under itself
    // or under one of its partners. `companyList` stays whole so deletions and
    // refetches keep working on the real data.
    visibleCompanyList() {
      return scopeCompaniesToCurrentBroker(this.companyList, this.brokerList);
    },
    // Users follow their entity: visible when they sit under a broker or a
    // company this broker already sees. `userList` stays whole so the broker
    // and company tabs keep resolving emails and representative names.
    visibleUserList() {
      return scopeUsersToCurrentBroker(this.userList, this.companyList, this.brokerList);
    },
    brokerElements() {
      const list = this.visibleBrokerList;
      return list.filter(li => li.id)
        .map((element) => {
          const originalBroker = this.brokerList.find(e => e.id === element.partmaster_id)
          const banks = element.banklist.map(bi => this.bankList.find(b => b.id === bi.id)).filter(Boolean).map(b => ({
            id: b.id,
            name: b.name,
            cnpj: b.cnpj,
            is_override: b.is_override ?? null,
          }))
          const brokerUser = this.userList.find(u => u.broker === element.id)
          const brokerEmail = brokerUser?.email 
          const rawFirstLogin = brokerUser?.first_login
          const firstLoginFormatted = rawFirstLogin ? this.formatDate(rawFirstLogin) : '-'

          const date = this.formatDate(element.timecreate);
          const result = {};
          result.id = element.id;
          result.user_id = element.user_id
          result.cnpj = convertToCNPJ(element.cnpj);
          result.email = brokerEmail;
          result.first_login = firstLoginFormatted;
          result.raw_first_login = rawFirstLogin;
          result.name = element.name;
          result.banklist = banks;
          result.partnerlist = element.partnerlist;
          result.broker_master = originalBroker?.name || '-'
          result.spread = element.spread_type === 'PIPS' ? element.spread : element.spread_bps
          result.spread_type = element.spread_type
          result.timecreate = date;
          result.raw_timecreate = element.timecreate;
          result.is_partner = (element.partmaster_id && element.partmaster_id !== 0 && element.partmaster_id !== '0') ? "Partner" : "";
          return result
        }).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    },
    bankElements() {
      const list = Array.isArray(this.bankList) ? this.bankList : [];
      return list.map((element) => {
        // Map element.brokerlist to rich broker objects
        const rawBrokerIds = Array.isArray(element.brokerlist) ? element.brokerlist : [];
        const brokers = rawBrokerIds.map((bId) => {
          const b = this.brokerList.find((broker) => broker.id === bId);
          return b
            ? {
                id: b.id,
                name: b.name,
                cnpj: convertToCNPJ(b.cnpj),
                email: b.email,
                partmaster_id: b.partmaster_id,
                is_partner: !!(b.partmaster_id && b.partmaster_id !== 0 && b.partmaster_id !== '0'),
              }
            : { id: bId, name: bId, cnpj: '-', is_partner: false };
        }).sort((a, b) => a.name.localeCompare(b.name));

        const date = this.formatDate(element.timecreate);

        const result = {};
        result.id = element.id;
        result.bankcode = element.bankcode;
        result.brokerlist = brokers;
        result.raw_brokerlist = rawBrokerIds;
        result.user_id = element.user_id;
        result.email = element.email;
        result.name = element.name;
        result.cnpj = convertToCNPJ(element.cnpj);
        result.is_override = element.is_override || 'bankauto';
        result.timecreate = date;
        result.raw_timecreate = element.timecreate;

        return result;
      }).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    },
    companyElements() {
      const list = this.visibleCompanyList;
      return list.map((element) => {
        const parentBroker = this.brokerList.find(b => b.id === element.broker)
        const hasBrokerId = !!(element.broker && element.broker !== 0 && element.broker !== '0')
        const brokerMasterName = parentBroker ? parentBroker.name : (hasBrokerId ? element.broker : '-')
        const companyUser = this.userList.find(u => u.company === element.id)
        const date = this.formatDate(element.timecreate);
        
        const banks = Array.isArray(element.banklist) 
          ? element.banklist.map(bi => {
              const b = this.bankList.find(bank => bank.id === bi.id)
              return b ? { id: b.id, name: b.name, cnpj: b.cnpj, is_override: b.is_override ?? null } : null
            }).filter(Boolean)
          : []

        return {
          id: element.id,
          user_id: companyUser?.id || element.user_id || '-',
          cnpj: convertToCNPJ(element.cnpj),
          name: element.name,
          representative_name: companyUser?.name || element.representative_name || '-',
          email: companyUser?.email || element.email || '-',
          broker_master: brokerMasterName,
          is_broker_resolved: !!parentBroker,
          is_parent_partner: parentBroker ? (parentBroker.partmaster_id && parentBroker.partmaster_id !== 0 && parentBroker.partmaster_id !== '0') : false,
          status: element.blocked === 1 ? "Paused" : "Active",
          lastTrade: element.lastTrade ? this.formatDate(element.lastTrade) : '-',
          raw_lastTrade: element.lastTrade,
          timecreate: date,
          raw_timecreate: element.timecreate,
          banklist: banks,
          parent_broker_id: element.broker,
          kyc_id: element.kyc_id,
          phone: element.phone || null,
          quotebot_link: element.quotebot_link || { status: 'unlinked', telegram_user_id: null, linked_at: null }
        }
      }).sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
    },
    deleteDependentsCount() {
      if (!this.deleteDependents) return 0
      return this.deleteDependents.partners.length + this.deleteDependents.companies.length
    },
    // A company in the delete modal is labelled with its owner only when the
    // owner is one of the partners going with it.
    deletePartnerNames() {
      const partners = this.deleteDependents ? this.deleteDependents.partners : []
      return partners.reduce((names, p) => ({ ...names, [p.id]: p.name }), {})
    },
  },
  methods: {
    ...mapActions(useExecutionStore, [
      "getBrokerById",
      "putBankById",
      "putBroker",
      "putCompany",
      "getCompanyStats",
      "updateKYC",
    ]),
    ...mapActions(useAdminStore, [
      "createBroker",
      "createClientUser",
      "userEntityDelete",
      "getBrokerList",
      "getBankList",
      "getUserList",
      "deleteBroker",
      "deleteBank",
      "getCompanyList",
      "createBankWithUser",
      "deleteUserById",
      "updateUserPassword",
    ]),
    async fetchBrokerList() {
      const { data } = await this.getBrokerList();
      this.brokerList = data;
      this.loadingTable = false
    },
    async fetchBankList() {
      const { data } = await this.getBankList();
      this.bankList = data;
      this.loadingTable = false
    },
    async fetchUserList() {
      const { data } = await this.getUserList();      
      this.userList = data;
      this.loadingTable = false
    },
    async fetchCompanyById() {
      const [companiesRes, statsRes] = await Promise.all([
        this.getCompanyList(),
        this.getCompanyStats()
      ])
      const list = Array.isArray(companiesRes.data) ? companiesRes.data : []
      const statsMap = statsRes.data || {}
      // Merge stats onto each company by id.
      list.forEach(c => Object.assign(c, statsMap[c.id] || {}))
      this.companyList = list
    },
    async downloadKYC(item) {
      this.loadingKYC = item.id;
      try {
        const zipStore = useImportFiles();
        await zipStore.downloadFiles('kyc', item.id, item.name, item.is_parent_partner);
      } catch (error) {
        console.error("Error downloading KYC: ", error);
        this.snackbar = {
          show: true,
          color: "error",
          message: "Failed to download KYC files."
        };
      } finally {
        this.loadingKYC = null;
      }
    },
    triggerKYCUpload(item) {
      this.kycUploadTargetCompany = item;
      if (this.$refs.companyDocumentInput) {
        this.$refs.companyDocumentInput.value = null;
        this.$refs.companyDocumentInput.click();
      }
    },
    async handleCompanyKYCUpload(event) {
      const files = Array.from(event.target.files || []);
      event.target.value = null;
      const company = this.kycUploadTargetCompany;
      this.kycUploadTargetCompany = null;
      if (!files.length || !company) return;

      const uploadStore = useUploadStore();
      const result = uploadStore.prepareUploadForKYC({
        filesList: files,
        id: company.id,
        minFiles: 0,
      });

      if (!result.ok || result.rejectedOversize?.length || result.extraFiles?.length) {
        const maxSizeMB = bytesToDisplayMB(result.maxSizeBytes || MAX_KYC_FILE_SIZE);
        const parts = [];
        if (result.rejectedOversize?.length) parts.push(`Files exceeding ${maxSizeMB} were skipped.`);
        if (result.extraFiles?.length) parts.push('Too many files selected, some were skipped.');
        this.confirmSnackBar(parts.join(' ') || 'Error validating files', 'red');
        if (!result.ok) return;
      }

      this.loadingKYCUpload = company.id;
      let kycId = company.kyc_id || null;
      const errors = [];

      for (const file of uploadStore.KYCFiles.docs) {
        try {
          const normalizedName = file.name.normalize('NFD').replace(/\p{Diacritic}/gu, '');
          const newFile = new File([file], normalizedName, { type: file.type });
          const response = await this.updateKYC(newFile, company.id, company.parent_broker_id, kycId);
          kycId = response.data?.kyc_id || kycId;
        } catch (e) {
          console.warn('error uploading KYC file', file.name, e?.message);
          errors.push(file.name);
        }
      }

      this.loadingKYCUpload = null;
      if (errors.length) {
        this.confirmSnackBar(`Failed to upload: ${errors.join(', ')}`, 'red');
      } else {
        this.confirmSnackBar('KYC files uploaded!');
      }
      this.fetchCompanyById();
    },
    formatChipColorAndBG(role){
      if(role === 'partner')
      {
        return {
          color: '#008000',
          background: '#0080005e'
        }
      }
      else if (role === 'broker') {
        return {
          color: '#0155bb',
          background: '#1f52915e'
        }
      }
      else if (role === 'bank') {
        return {
        color: '#e53935',
        background: '#e539355e'
        }
      }

      return {
        color: '#4D4D4D',
        background: '#1C1C1C'
      } 
    },
    exportBrokers(){
      generateBrokerJSON(this.visibleBrokerList)
    },

    handleOpenDropDownMenu(item) {
      if (!item) { this.openSelectedId = null; return; }
      if (this.openSelectedId && this.openSelectedId !== item.id) {
        const prevRef = this.$refs['bankSelect-' + this.openSelectedId]
        const prev = Array.isArray(prevRef) ? prevRef[0] : prevRef
        if (prev) prev.isMenuActive = false
      }
      this.openSelectedId = this.openSelectedId === item.id ? null : item.id
      
    },
    async updateBroker(item, type, bankId){
      // Match broker by ID instead of stripping CNPJ
      const rawBroker = this.brokerList.find(b => b.id === item.id)
      if (!rawBroker) {
        this.confirmSnackBar('Broker not found', 'red')
        return
      }

      this.loadingBankBrokerId = item.id
      let banklist = [...(rawBroker.banklist || [])]
      const bankObj = this.bankList.find(b => b.id === bankId)

      if (type === 'delete') {
        banklist = banklist.filter(b => b.id !== bankId)
      } else if (type === 'add' && !banklist.some(b => b.id === bankId)) {
        if (!bankObj) {
          this.confirmSnackBar('Bank not found', 'red')
          this.loadingBankBrokerId = null
          return
        }
        banklist.push({
          id: bankId,
          name: bankObj.name,
          email: bankObj?.email || '',
          timeDue: bankObj.timeDue ?? null,
          isOverride: bankObj.is_override
        })
      }

      try {
        await this.updateBanksList(rawBroker.id, banklist.map(b => b.id))
        await this.putBroker({ banklist }, rawBroker.id)

        const msg = type === 'delete' ? 'Bank Deleted!' : 'Bank Added!'
        this.confirmSnackBar(msg)

        await Promise.all([
          this.fetchBrokerList(),
          this.fetchBankList()
        ])
      } catch (e) {
        console.error('Error updating broker banks:', e)
        this.confirmSnackBar(e?.response?.data?.detail || 'Error updating broker banks', 'red')
      } finally {
        this.loadingBankBrokerId = null
      }
    },
    handleBankMode(mode, bankId) {
      if (!mode) return
      this.loadingBankMode = bankId
      this.pendingBankMode = mode
      this.putBankById(bankId, { is_override: mode })
        .then(() => {
          const bank = this.bankList.find(b => b.id === bankId)
          if (bank) bank.is_override = mode
          return this.syncBankModeToLinkedEntities(bankId, mode)
        })
        .then(() => {
          this.confirmSnackBar('Bank mode updated!')
        })
        .catch((e) => {
          console.warn('error updating bank mode', e?.message)
          this.confirmSnackBar(e?.response?.data?.detail || 'Error updating bank mode', 'red')
          // The toggle keeps the mode that was clicked, and only a new value
          // resets it; the bank's mode has not changed, so redraw the toggles.
          this.bankModeRedraws += 1
        })
        .finally(() => {
          this.loadingBankMode = null
          this.pendingBankMode = null
        })
    },
    // Every broker/company keeps its own denormalized copy of a linked bank's mode
    // (set at link-time in updateBroker/updateCompany). Changing the bank's mode here
    // only updates the bank record itself, so without this, entities that already linked
    // the bank keep serving the stale mode (e.g. to the trading/RFQ screens) until
    // someone re-links the bank.
    //
    // Each record is read again just before it is written, and only this bank's
    // mode changes in it: the copy this page holds can be 2 s old, and writing
    // it back would undo a newer change to the same list, such as a PRO's
    // selection. A record that no longer lists the bank is left alone.
    syncBankModeToLinkedEntities(bankId, mode) {
      const withMode = banklist => banklist.map(bl => bl.id === bankId ? { ...bl, isOverride: mode } : bl)
      const listsBank = banklist => Array.isArray(banklist) && banklist.some(bl => bl.id === bankId)

      const brokerUpdates = this.brokerList
        .filter(b => listsBank(b.banklist))
        .map(async b => {
          try {
            const stored = (await this.getBrokerById(b.id)).data?.banklist
            if (!listsBank(stored)) return
            b.banklist = withMode(stored)
            await this.putBroker({ banklist: b.banklist }, b.id)
          } catch (e) {
            console.warn('error syncing bank mode to broker', b.id, e?.message)
          }
        })

      const companyUpdates = this.companyList
        .filter(c => listsBank(c.banklist))
        .map(async c => {
          try {
            const stored = (await useExecutionStore().getCompanyById(c.id)).data?.banklist
            if (!listsBank(stored)) return
            c.banklist = withMode(stored)
            await this.putCompany({ id: c.id, banklist: c.banklist })
          } catch (e) {
            console.warn('error syncing bank mode to company', c.id, e?.message)
          }
        })

      return Promise.all([...brokerUpdates, ...companyUpdates])
    },
    toggleOverride(i, j)
    {
      const override = {"is_override" : j.is_override}
      this.putBankById(j.id, override)
        .then(() => {
          const text = i.is_override ? 'Override Bank is On!' : 'Override Bank is Off!'
          this.confirmSnackBar(text)
        })
    },
    availableBrokersForBanks(bank) {
      if (!bank) return []
      const list = bank.raw_brokerlist || bank.brokerlist || []
      const linkedIdSet = new Set(list.map(b => (typeof b === 'object' && b ? b.id : b)))

      return this.visibleBrokerList.filter(b => !linkedIdSet.has(b.id))
    },
    availableBanksForBroker(broker) {
      if (!broker) return []
      const linkedIdSet = new Set((broker.banklist || []).map(b => b.id))
      // A partner may only use banks its master has.
      const partmasterId = this.brokerList.find(b => b.id === broker.id)?.partmaster_id
      if (partmasterId && partmasterId !== '0') {
        const master = this.brokerList.find(b => b.id === partmasterId)
        const masterBankIds = new Set((master?.banklist || []).map(b => b.id))
        return this.bankList.filter(b => masterBankIds.has(b.id) && !linkedIdSet.has(b.id))
      }

      return this.bankList.filter(b => !linkedIdSet.has(b.id))
    },
    availableBanksForCompany(company) {
      if (!company) return []
      const parentBroker = this.brokerList.find(b => b.id === company.parent_broker_id)
      if (!parentBroker || !parentBroker.banklist) return [];
      const parentBankIds = new Set(parentBroker.banklist.map(b => b.id))
      const companyBankIds = new Set((company.banklist || []).map(b => b.id))
      return this.bankList.filter(b => parentBankIds.has(b.id) && !companyBankIds.has(b.id))
    },
    async updateCompany(item, type, bankId) {
      // Match company by ID instead of stripping CNPJ
      const rawCompany = this.companyList.find(c => c.id === item.id)
      if (!rawCompany) {
        this.confirmSnackBar('Company not found', 'red')
        return
      }

      this.loadingBankCompanyId = item.id
      let banklist = [...(rawCompany.banklist || [])]
      const bankObj = this.bankList.find(b => b.id === bankId)

      if (type === 'delete') {
        banklist = banklist.filter(b => b.id !== bankId)
      } else if (type === 'add' && !banklist.some(b => b.id === bankId)) {
        if (!bankObj) {
          this.confirmSnackBar('Bank not found', 'red')
          this.loadingBankCompanyId = null
          return
        }
        banklist.push({
          id: bankId,
          name: bankObj.name,
          email: bankObj?.email || '',
          timeDue: bankObj.timeDue ?? null,
          isOverride: bankObj.is_override
        })
      }

      try {
        await this.putCompany({
          id: rawCompany.id,
          banklist: banklist
        })
        const msg = type === 'delete' ? 'Bank Deleted!' : 'Bank Added!'
        this.confirmSnackBar(msg)
        await this.fetchCompanyById()
      } catch (e) {
        console.error('Error updating company banks:', e)
        this.confirmSnackBar(e?.response?.data?.detail || 'Error updating company banks', 'red')
      } finally {
        this.loadingBankCompanyId = null
      }
    },
    openBankBrokersModal(item) {
      this.selectedBankForBrokers = item
      this.bankBrokersModal = true
    },
    openEntityBanksModal(item, type) {
      this.selectedEntityForBanks = item
      this.selectedEntityType = type
      this.entityBanksModal = true
    },
    async handleAddEntityBank({ entity, type, bankId, onComplete }) {
      if (type === 'broker') {
        await this.updateBroker(entity, 'add', bankId)
        if (this.selectedEntityForBanks && this.selectedEntityForBanks.id === entity.id) {
          this.selectedEntityForBanks = this.brokerElements.find(b => b.id === entity.id) || this.selectedEntityForBanks
        }
      } else {
        await this.updateCompany(entity, 'add', bankId)
        if (this.selectedEntityForBanks && this.selectedEntityForBanks.id === entity.id) {
          this.selectedEntityForBanks = this.companyElements.find(c => c.id === entity.id) || this.selectedEntityForBanks
        }
      }
      if (onComplete) onComplete()
    },
    async handleRemoveEntityBank({ entity, type, bankId, onComplete }) {
      if (type === 'broker') {
        await this.updateBroker(entity, 'delete', bankId)
        if (this.selectedEntityForBanks && this.selectedEntityForBanks.id === entity.id) {
          this.selectedEntityForBanks = this.brokerElements.find(b => b.id === entity.id) || this.selectedEntityForBanks
        }
      } else {
        await this.updateCompany(entity, 'delete', bankId)
        if (this.selectedEntityForBanks && this.selectedEntityForBanks.id === entity.id) {
          this.selectedEntityForBanks = this.companyElements.find(c => c.id === entity.id) || this.selectedEntityForBanks
        }
      }
      if (onComplete) onComplete()
    },
    async handleAddBankBroker({ bank, brokerId, onComplete }) {
      this.loadingBankBrokers = true
      try {
        const rawBank = this.bankList.find(b => b.id === bank.id)
        const currentBrokers = Array.isArray(rawBank?.brokerlist) ? [...rawBank.brokerlist] : []
        if (currentBrokers.includes(brokerId)) return

        const newBrokerlist = [...currentBrokers, brokerId]
        await this.putBankById(bank.id, { brokerlist: newBrokerlist })

        this.confirmSnackBar('Broker linked!')
        if (onComplete) onComplete()

        await Promise.all([
          this.fetchBankList(),
          this.fetchBrokerList(),
          this.fetchCompanyById()
        ])
        if (this.selectedBankForBrokers && this.selectedBankForBrokers.id === bank.id) {
          this.selectedBankForBrokers = this.bankElements.find(b => b.id === bank.id) || this.selectedBankForBrokers
        }
      } catch (e) {
        console.error('Error linking broker to bank:', e)
        this.confirmSnackBar(e?.response?.data?.detail || 'Error linking broker to bank', 'red')
      } finally {
        this.loadingBankBrokers = false
        if (onComplete) onComplete()
      }
    },
    async handleRemoveBankBroker({ bank, brokerId, onComplete }) {
      this.loadingBankBrokers = true
      try {
        const rawBank = this.bankList.find(b => b.id === bank.id)
        const currentBrokers = Array.isArray(rawBank?.brokerlist) ? rawBank.brokerlist : []
        const newBrokerlist = currentBrokers.filter(id => id !== brokerId)

        await this.putBankById(bank.id, { brokerlist: newBrokerlist })

        this.confirmSnackBar('Broker unlinked!')
        if (onComplete) onComplete()

        await Promise.all([
          this.fetchBankList(),
          this.fetchBrokerList(),
          this.fetchCompanyById()
        ])
        if (this.selectedBankForBrokers && this.selectedBankForBrokers.id === bank.id) {
          this.selectedBankForBrokers = this.bankElements.find(b => b.id === bank.id) || this.selectedBankForBrokers
        }
      } catch (e) {
        console.error('Error unlinking broker from bank:', e)
        this.confirmSnackBar(e?.response?.data?.detail || 'Error unlinking broker from bank', 'red')
      } finally {
        this.loadingBankBrokers = false
        if (onComplete) onComplete()
      }
    },
    resetPassword(item)
    {
      const email = item.email && item.email !== '-' ? item.email : '';
      if (!email) {
        this.confirmSnackBar('No email associated with this item', 'red');
        return;
      }

      this.deleteObj.id = item.id
      this.deleteObj.email = email
      this.deleteObj.cnpj = item.cnpj;
      this.resetPasswordModal = true
    },
    confirmSnackBar(msg, color = 'green') {
      this.snackbar.show = true;
      this.snackbar.message = msg;
      this.snackbar.color = color
      this.loadingDelete = false;
      this.loadingReset = false
    },
    //deleting
    async openDeleteModal(item, tableRole) {
      this.deleteObj.id = tableRole !== 'user' ?  item.id : item.bank != '-' ? item.bank : item.broker !== '-' ? item.broker : item.company
      this.deleteObj.user_id = tableRole !== 'user' ? item.user_id : item.id
      this.deleteObj.email = item.email ?? '';
      this.deleteObj.cnpj = item.cnpj?.replace(/\D/g, "");
      this.deleteObj.type = tableRole;
      this.deleteObj.role = item.role
      // A user's delete takes the broker, company or bank the user belongs to.
      this.deleteObj.entityTitle = tableRole === 'user' && item.entity_name !== '-' ? item.entity_title : ''
      this.deleteDependents = null
      // Deleting a broker takes its partners and every company under it or
      // them, and deleting a broker's user takes the broker. The backend lists
      // those from the same walk its delete does, so the modal shows exactly
      // what goes.
      const brokerId = tableRole === 'broker' ? item.id
        : tableRole === 'user' && item.broker !== '-' ? item.broker : null
      if (brokerId) {
        try {
          const { data } = await useAdminStore().getBrokerDeletePreview(brokerId)
          this.deleteDependents = data
        } catch (e) {
          // 404: the broker is already gone, so the delete takes nothing else.
          if (e?.response?.status !== 404) {
            this.confirmSnackBar(e?.response?.data?.detail || 'Could not check what this delete would remove', 'red')
            return
          }
        }
      }
      this.confirmDeleteModal = true;
    },
    confirmToggleStatus(item) {
      this.statusToggleItem = item;
      this.confirmStatusModal = true;
    },
    toggleCompanyStatus() {
      if (!this.statusToggleItem) return;
      this.loadingStatusToggle = true;
      const nextBlocked = this.statusToggleItem.status === 'Active' ? 1 : 0;
      this.putCompany({
        id: this.statusToggleItem.id,
        blocked: nextBlocked
      })
      .then(() => {
        this.confirmSnackBar('Status Updated!');
        this.fetchCompanyById();
      })
      .catch((e) => {
        console.error('Error toggling company status', e);
        this.confirmSnackBar(e?.response?.data?.detail || 'Error updating status', 'red');
      })
      .finally(() => {
        this.loadingStatusToggle = false;
        this.confirmStatusModal = false;
        this.statusToggleItem = null;
      });
    },
    formatDeleteRole(){
      if(this.deleteObj.type === 'user')
      {
        return this.deleteObj.role === 'pro' ? this.deleteObj.role = 'company' : this.deleteObj.role
      }
      return this.deleteObj.type
    },
  confirmDeleteEntity() {
      this.loadingDelete = true;
      if (this.deleteObj.type === 'user') {
        this.deleteUserById(this.deleteObj.user_id)
          .then((res) => {
             this.updateCompanyList()
             this.confirmSnackBar('Deleted')
             this.confirmDeleteModal = false
          })
          .catch((e) => {
             this.confirmSnackBar(`${e?.message || 'error while deleting'}`, 'red')
             console.error('ERROR while deleting', e?.message)
          })
        return
      }
      const role = this.formatDeleteRole()
      this.userEntityDelete(role, this.deleteObj.id)
        .then((res)=>{
            this.updateCompanyList()
            this.confirmSnackBar(`Deleted`);
            this.confirmDeleteModal = false;
        })
        .catch((e)=>{
          this.confirmSnackBar(`${e?.message || 'error while deleting'}`, 'red');
          console.error('ERROR while deleting', e?.message)
        })
    },
    resetIt(v) {
      if (!this.deleteObj.email || this.deleteObj.email === '-') {
        this.confirmSnackBar('No email associated with this item', 'red');
        this.resetPasswordModal = false;
        return;
      }
      this.loadingReset = true;
      this.updateUserPassword(v, this.deleteObj.email)
        .then(() => {
          this.confirmSnackBar('Password Updated!');
          this.resetPasswordModal = false;
        })
        .catch(e => {
          console.error('Error updating password:', e);
          this.confirmSnackBar(e?.response?.data?.detail || e?.message || 'Error updating password', 'red');
        })
        .finally(() => {
          this.loadingReset = false;
        });
    },
    updateCompanyList()
    {
        this.userList = this.userList.filter(u=>u.id !== this.deleteObj.user_id)
        this.companyList = this.companyList.filter(u=>u.id !== this.deleteObj.id)
    },
    formatDate(time) {
      return formatShortDate(time);
    },
    formatTimeAndDate(epoch) {
      return formatTimeAndDate(epoch);
    },
    isOnline(lastOnlineEpoch) {
      if (!lastOnlineEpoch) return false;
      return (Date.now() / 1000 - lastOnlineEpoch) <= 15 * 60;
    },
    formatTimePart(epoch) {
      if (!epoch) return "";
      const toMs = v => {
        const n = Number(v);
        if (!isFinite(n)) return Date.now();
        return n > 1e12 ? n : Math.round(n * 1000);
      };
      const date = new Date(toMs(epoch));
      const timeZone = this.settingsStore.timezone === 'BRT' ? 'America/Sao_Paulo' : 'UTC';
      const timezoneLabel = this.settingsStore.timezone;
      
      const timeOptions = {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        timeZone
      };
      
      const formattedTime = new Intl.DateTimeFormat('pt-BR', timeOptions).format(date);
      return `${formattedTime} ${timezoneLabel}`;
    },
    addBrokerOrBank(type) {
      this.createBrokerModal = true;
      this.creationType = type;
    },
    updateBanksList(brokerId, bankIds = []) {
      const updateBanks = this.bankList.map(b => {
        const shouldHaveBroker = bankIds.includes(b.id)
        const hasBroker = (b.brokerlist || []).includes(brokerId)

        if (shouldHaveBroker && !hasBroker) {
          return this.putBankById(b.id, {
            brokerlist: [...(b.brokerlist || []), brokerId]
          })
        }
        if (!shouldHaveBroker && hasBroker) {
          return this.putBankById(b.id, {
            brokerlist: (b.brokerlist || []).filter(id => id !== brokerId)
          })
        }
        return null
      }
      ).filter(Boolean)

      return Promise.all(updateBanks)

    },
    handleCreateBroker(payload) {
      if (this.creationType === "broker") {
        // The banks are linked from the bank side by updateBanksList. Sent with
        // the broker, their bare ids would be stored in broker.banklist, whose
        // entries are {id, name, timeDue}.
        const { banklist, ...broker } = payload
        this.createBroker(broker)
          .then((res) => {
            this.creationType = null
            if (!banklist?.length) {
              this.confirmSnackBar('User successfully created')
              return
            }
            // The broker and its user exist by now, so a failure from here on
            // is only the bank links, and the message says so.
            return this.updateBanksList(res.data.id, banklist)
              .then(() => this.confirmSnackBar('User successfully created'))
              .catch((e) => this.confirmSnackBar(
                `User created, but linking its banks failed: ${e?.response?.data?.detail || e}`, 'red'))
          })
          .catch((e) => {
            this.confirmSnackBar(`ERROR in creating user, ${e?.response?.data?.detail || e}`, 'red')
          })
          .finally(() => {
            this.createBrokerModal = false;
          })

      } else if (this.creationType === "bank") {
        this.createBankWithUser(payload)
          .then(() => {
            this.confirmSnackBar("User successfully created");
          })
          .catch((e) => {
            this.confirmSnackBar(`ERROR in creating user, ${e?.response?.data?.detail || e}`, 'red')
          })
          .finally(() => {
            this.createBrokerModal = false;
            this.creationType = null;
          });
      }
    },
    getStatusColor(status) {
      const s = status.toLowerCase();
      if (s === "active" || s === "ativo") return "green";
      if (s === "deal" || s === "negocio") return "green";
      if (s === "paused" || s === "pausado") return "orange lighten-3";
      if (s === "pending auth" || s === "pendente autenticação")
        return "blue lighten-4";
      if (s === "pending" || s === "pendente") return "orange darken-2";
      return "grey darken-1";
    },
    openPhoneModal(item) {
      this.selectedPhoneCompany = item;
      this.phoneModal = true;
    },
  },
};
</script>

<style scoped>
::v-deep .v-data-table>.v-data-table__wrapper>table>tbody>tr>td {
  height: 35px;
}

::v-deep .v-data-table>.v-data-table__wrapper>table>tbody>tr>td span {
  /*
  We have a global !important CSS rule for `...>tr>td`
  If we use a slot to render a cell ourselves, that rule would break.
  So here we apply the same `font-size` to make text sizes consistent.
  */
  font-size: 0.875rem !important;
}

.v-data-table table {
  max-width: 100%;
  display: flex;
  flex-direction: column;
}

.cnpj-title {
  font-size: var(--cnpj-font-size) !important;
  white-space: nowrap;
}

::v-deep .v-data-table {
  padding: 0 !important;
}

.truncated-name {
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.truncated-rep-name {
  display: inline-block;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

::v-deep .v-select .v-input__control {
  min-height: 20px !important;
  width: 30px !important;
}

::v-deep .v-text-field__details {
  display: none;
}

.chip {
  width: 60px;
  padding: 0;
  justify-content: center;
}

::v-deep .none {
  background: none !important;
}

::v-deep .v-list-item__action {
  flex: 1 1 auto !important;
}

::v-deep .select-bank .v-input__control .v-input__slot {
  visibility: hidden !important;
}

.delete-dependents {
  max-height: 240px;
  overflow-y: auto;
}

.broker-count-btn {
  height: 32px !important;
  min-width: 60px !important;
}

.broker-count-text {
  font-size: 1rem !important;
  font-weight: 600;
}
</style>
